import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { AuthService } from "./";
import { toast } from "react-toastify";

const language = localStorage.getItem("language") || "en";
const baseUrl = process.env.REACT_APP_API_URL;

const instance = axios.create({
  baseURL: baseUrl || "https://api.dev.bemyticket.com",
  headers: {
    "Accept-Language": language,
  },
});


const SESSION_TOAST_ID = "session-expired-toast";


let isRefreshing = false;
let subscribers: Array<(token: string) => void> = [];

function addSubscriber(callback: (token: string) => void) {
  subscribers.push(callback);
}

function onAccessTokenFetched(token: string) {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
}

function isTokenExpiredError(error: AxiosError<any>): boolean {
  const status = error.response?.status;

  if (status !== 401) return false;

  const data = error.response?.data;

  const description: string | undefined =
    data?.error_description || data?.message || data?.error;

  return (
    typeof description === "string" &&
    (description.includes("Access token expired") ||
      description.includes("invalid_token") ||
      description.includes("token expired"))
  );
}

instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = localStorage.getItem("jwt_access_token");
    config.withCredentials = false;

    const url = config.url || "";

    const isTokenEndpoint =
      url.includes("/oauth/token") || url.includes("/token") || url.includes("/refresh");

    const isDownload =
      config.responseType === "blob" ||
      config.responseType === "arraybuffer" ||
      url.includes("/pdf") ||
      url.includes("/xls");

    config.headers = {
      ...(config.headers || {}),
      "Accept-Language": language,
    };

    if (!(config.headers as any).Accept) {
      (config.headers as any).Accept = isDownload ? "*/*" : "application/json";
    }

    const method = (config.method || "get").toLowerCase();
    const hasBody = ["post", "put", "patch"].includes(method);

    if (hasBody && !(config.headers as any)["Content-Type"]) {
      (config.headers as any)["Content-Type"] = "application/json";
    }

    if (token && !isTokenEndpoint) {
      (config.headers as any).Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

instance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<any>) => {

    if (!error.response) {
      if (error.message === "Network Error") {
        toast.error("You are offline. Please check your internet connection.");
      }
      return Promise.reject(error);
    }

    const originalRequest: any = error.config;

  
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isTokenExpiredError(error)) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          addSubscriber((newToken: string) => {
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${newToken}`,
            };
            resolve(instance(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshResponse: any = await AuthService.refresh_token();

        const newAccessToken = refreshResponse?.data?.access_token;
        const newRefreshToken = refreshResponse?.data?.refresh_token;

        if (!newAccessToken) {
          throw new Error("Refresh succeeded but access_token is missing");
        }

        AuthService.setSession(newAccessToken, newRefreshToken);
        isRefreshing = false;

        onAccessTokenFetched(newAccessToken);
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newAccessToken}`,
        };

        return instance(originalRequest);
      } catch (refreshErr) {
        isRefreshing = false;
        subscribers = [];

        if (!toast.isActive(SESSION_TOAST_ID)) {
          toast.error("Your session has expired. Please log in again.", {
            toastId: SESSION_TOAST_ID,
          });
        }

        try {
          AuthService.logout();
        } catch (_) {
       
        }

        localStorage.clear();
        window.location.href = "/login";

        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
